import React, { useRef, useState } from 'react';
import {
  Box, Button, Stack, Snackbar, Alert, Dialog, DialogTitle, DialogContent, 
  DialogActions, Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, Paper, Typography, FormControlLabel, Switch, LinearProgress,
  Backdrop, CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import CancelIcon from '@mui/icons-material/Cancel';
import { API_BASE_URL } from 'envConfig';
import { LocalStorageAuth } from 'utils/LocalStorageAuth';

const CsvManager: React.FC<{
  collectionName: string;
  canUpload?: boolean;
  canDownload?: boolean;
  onUploadComplete?: () => void;
}> = ({ collectionName, canUpload = true, canDownload = true, onUploadComplete }) => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [errorDialog, setErrorDialog] = useState({ open: false, errors: [] as any[], successCount: 0, errorCount: 0, updateCount: 0, insertCount: 0, deleteCount: 0 });
  const [useSmartUpload, setUseSmartUpload] = useState(false);
  
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const showMessage = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const uploadFile = (file: File) => {
    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    const endpointPath = useSmartUpload 
      ? `/iu/ds/cmp/set/entry/smartuploadcsv/${collectionName}`
      : `/iu/ds/cmp/set/entry/uploadcsv/${collectionName}`;

    const uploadUrl = `${API_BASE_URL}${endpointPath}`;

    const xhr = new XMLHttpRequest();
    
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        setUploadProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const responseData = JSON.parse(xhr.responseText);
          const responseDataObj = responseData.data || responseData;

          let message = '';
          
          if (useSmartUpload) {
            const { updateCount, insertCount, deleteCount, errorCount } = responseDataObj;
            message = `Smart CSV upload completed! Updated: ${updateCount || 0}, Added: ${insertCount || 0}, Deleted: ${deleteCount || 0}`;
            if (errorCount > 0) {
              message += `. ${errorCount} errors occurred.`;
            }
          } else {
            const { successCount, errorCount } = responseDataObj;
            message = `CSV uploaded successfully! ${successCount || 0} records saved.`;
            if (errorCount > 0) {
              message += ` ${errorCount} rows had errors.`;
            }
          }
          
          if (responseDataObj.errorCount > 0) {
            showMessage(`${message}`, 'success');
            setErrorDialog({
              open: true,
              errors: responseDataObj.errors || [],
              successCount: responseDataObj.successCount || 0,
              errorCount: responseDataObj.errorCount || 0,
              updateCount: responseDataObj.updateCount || 0,
              insertCount: responseDataObj.insertCount || 0,
              deleteCount: responseDataObj.deleteCount || 0
            });
          } else {
            showMessage(message, 'success');
          }

          onUploadComplete?.();
        } catch (error) {
          console.error('Error parsing response:', error);
          showMessage('CSV upload completed but response parsing failed', 'error');
        }
      } else {
        let errorMessage = 'CSV upload failed';
        try {
          const responseData = JSON.parse(xhr.responseText);
          if (responseData.message) {
            errorMessage = responseData.message;
          }
          if (responseData.data?.errors && responseData.data.errors.length > 0) {
            errorMessage += `. ${responseData.data.errors.length} rows had errors.`;
            setErrorDialog({
              open: true,
              errors: responseData.data.errors || [],
              successCount: responseData.data.successCount || 0,
              errorCount: responseData.data.errorCount || 0,
              updateCount: responseData.data.updateCount || 0,
              insertCount: responseData.data.insertCount || 0,
              deleteCount: responseData.data.deleteCount || 0
            });
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
        }
        showMessage(errorMessage, 'error');
      }
      setIsUploading(false);
      setUploadProgress(0);
    });

    xhr.addEventListener('error', () => {
      console.error('Network error during upload');
      showMessage('CSV upload failed due to network error', 'error');
      setIsUploading(false);
      setUploadProgress(0);
    });

    xhr.addEventListener('timeout', () => {
      console.error('Upload timeout');
      showMessage('CSV upload failed due to timeout', 'error');
      setIsUploading(false);
      setUploadProgress(0);
    });

    xhr.open('PUT', uploadUrl, true);
    
    const token = LocalStorageAuth.getToken();
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    
    xhr.send(formData);
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleDownload = async () => {
    try {
      const downloadUrl = `/iu/ds/cmp/set/entry/downloadcsv/${collectionName}`;
      const response = await fetch(`${API_BASE_URL}${downloadUrl}`, {
        headers: {
          'Authorization': `Bearer ${LocalStorageAuth.getToken()}`,
        },
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${collectionName}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error(error);
      showMessage('CSV download failed', 'error');
    }
  };

  return (
    <>
      <Box sx={{ 
        display: "flex", 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 2 },
        alignItems: { xs: 'stretch', sm: 'center' },
        flexWrap: 'wrap'
      }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={{ xs: 1, sm: 1 }} 
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ flexWrap: 'wrap' }}
        >
          {canUpload && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelected}
                style={{ display: 'none' }}
              />
              <Button
                variant="contained"
                color="secondary"
                startIcon={<CloudUploadIcon />}
                onClick={handleUploadClick}
                disabled={isUploading}
                size="small"
                sx={{ 
                  minWidth: { xs: '100%', sm: 'auto' },
                  whiteSpace: 'nowrap'
                }}
              >
                {useSmartUpload ? 'Smart Upload CSV' : 'Upload CSV'}
              </Button>
            </>
          )}

          {canDownload && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              size="small"
              sx={{ 
                minWidth: { xs: '100%', sm: 'auto' },
                whiteSpace: 'nowrap'
              }}
            >
              Download CSV
            </Button>
          )}
        </Stack>

        {canUpload && (
          <FormControlLabel
            control={
              <Switch
                checked={useSmartUpload}
                onChange={(e) => setUseSmartUpload(e.target.checked)}
                color="secondary"
                size="small"
              />
            }
            label={
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                Smart Upload: Update existing records, add new ones, delete missing ones
              </Typography>
            }
            sx={{ 
              minWidth: { xs: '100%', sm: 'auto' },
              '& .MuiFormControlLabel-label': {
                fontSize: '0.75rem'
              }
            }}
          />
        )}
      </Box>

      {isUploading && (
        <>
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={uploadProgress} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                }
              }}
            />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1, textAlign: 'center' }}>
              Uploading CSV... {uploadProgress}%
            </Typography>
          </Box>
          
          <Backdrop
            sx={{ 
              color: '#fff', 
              zIndex: (theme) => theme.zIndex.drawer + 1,
              backgroundColor: 'rgba(0, 0, 0, 0.7)'
            }}
            open={isUploading}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: 2 
            }}>
              <CircularProgress color="inherit" size={60} />
              <Typography variant="h6" color="inherit">
                Uploading CSV File...
              </Typography>
              <Typography variant="body2" color="inherit" sx={{ opacity: 0.8 }}>
                {uploadProgress}% Complete
              </Typography>
            </Box>
          </Backdrop>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={errorDialog.open}
        onClose={() => setErrorDialog({ ...errorDialog, open: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          CSV Upload Results
          {(errorDialog.updateCount > 0 || errorDialog.insertCount > 0 || errorDialog.deleteCount > 0) && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Updated: {errorDialog.updateCount} | Added: {errorDialog.insertCount} | Deleted: {errorDialog.deleteCount}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {errorDialog.errors.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                Errors ({errorDialog.errorCount})
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Error</TableCell>
                      <TableCell>Data</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {errorDialog.errors.map((error, index) => (
                      <TableRow key={index}>
                        <TableCell>{error.row}</TableCell>
                        <TableCell>{error.error}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {JSON.stringify(error.data, null, 2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={() => setErrorDialog({ ...errorDialog, open: false })}
            startIcon={<CancelIcon />}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CsvManager;
